import type { ReactNode } from 'react';
import type { BadgeType } from '../../atoms/Badge/Badge';
import { CardHeader } from '../CardHeader/CardHeader';
import styles from './TableHeader.module.css';

/**
 * Figma: `Table Header` (336:23513) — "Top bar for data tables with title,
 * search, and action buttons… Rounds only the top corners so it sits flush
 * above table rows and pairs with a bottom-rounded Pagination footer."
 *
 * **The title is a `Card Header` instance now.** It used to be a bare `Text
 * Wrapper` this component drew itself; the 2026-09 rework put the real
 * component in the slot, so the two are no longer parallel structures that
 * happen to look alike — one contains the other. Everything that arrived with
 * that rework comes along for free: the dashed underline, the help trigger
 * *leading* the title rather than trailing it, its `globe` glyph, and the title
 * dropping from `text/primary` to `text/secondary` on `leading-none`. Composing
 * it is also what stops the two drifting apart again.
 *
 * **`supportingText` is gone with it.** Figma's instance still sets
 * `Supporting text=true`, but that boolean references no layer in `Card Header`
 * any more, so nothing can turn it on — the same dead property recorded for
 * `CardHeader` itself. Removed rather than kept as a code-only extra. See
 * plan.md §7.
 *
 * The instance is `Actions=false`: `Card Header`'s own trailing slot stays
 * empty, and the search and buttons live in `Table Header`'s own `Right
 * Wrapper`, a sibling of the instance. So `search` and `actions` stay here.
 *
 * They are slots rather than typed props because Figma fills them with whole
 * instances: a `Search` field, and four buttons across four different
 * hierarchies. Naming them here would mean re-describing two components this one
 * does not own.
 *
 * The search slot held a `Select` in search mode until 2026-09-17, when the
 * designer drew `Search` (990:37054) as its own component and instanced it here.
 * The two are not interchangeable: `Select` filters a menu and returns one
 * chosen option, `Search` reports a free-text query. Call sites moved with it.
 */
export type TableHeaderProps = {
  /** Figma `Text` on the nested `Card Header` — the title. */
  title: string;
  /**
   * Figma `Badge` on the nested `Card Header` — a gray count after the title,
   * "50 total pages" on the Competitors frame (2030:19055). Forwarded as-is;
   * see `CardHeader`'s `badge`.
   */
  badge?: string;
  /** Overrides the badge's `Type` — see `CardHeader`'s `badgeType`. */
  badgeType?: BadgeType;
  /** An `id` for the title — see `CardHeader`'s `titleId`. Not in Figma. */
  titleId?: string;
  /** Bumped on a magnetic catch — see `CardHeader.flash`. */
  flash?: number;
  /**
   * Figma's `Help` instance inside `Card Header` — the tooltip body. Omit for no
   * help icon, which also drops the underline: without an explanation behind it
   * the affordance promises something that is not there.
   */
  helpText?: string;
  /** Second line in the help tooltip — Figma `Supporting text` on that instance. */
  helpSupportingText?: string;
  /** Figma `Search?` — takes a `Search` field. */
  search?: ReactNode;
  /** Figma `Actions` — the trailing button group. */
  actions?: ReactNode;
  className?: string;
};

export function TableHeader({
  title,
  badge,
  badgeType,
  titleId,
  flash,
  helpText,
  helpSupportingText,
  search,
  actions,
  className,
}: TableHeaderProps) {
  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      <div className={styles.content}>
        <CardHeader
          title={title}
          badge={badge}
          badgeType={badgeType}
          titleId={titleId}
          flash={flash}
          helpText={helpText}
          helpSupportingText={helpSupportingText}
          className={styles.cardHeader}
        />

        {(search !== undefined || actions !== undefined) && (
          <div className={styles.rightWrapper}>
            {search !== undefined && <div className={styles.search}>{search}</div>}
            {actions !== undefined && <div className={styles.actions}>{actions}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
